// ═══════════════════════════════════════════════════════════════════════════════
// iOS PWA — textarea focus/blur scroll restore after software keyboard closes
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import {
  captureScrollBeforeTextareaFocus,
  resetIosPwaTextareaKeyboardRecovery,
  scheduleIosPwaTextareaKeyboardRecovery,
} from '../lib/pwaTextareaKeyboardRecovery';
import { isVirtualKeyboardOpen } from '../lib/pwaShellHeight';
import { isIosMobilePwa } from '../lib/pwaRuntime';

/** iOS PWA only — restore inner scroll when textarea keyboard dismisses. */
export const useIosPwaTextareaKeyboardRecovery = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || !isIosMobilePwa()) {
      return undefined;
    }

    const handleFocusIn = (event: FocusEvent): void => {
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement)) {
        return;
      }
      captureScrollBeforeTextareaFocus();
    };

    const handleFocusOut = (event: FocusEvent): void => {
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement)) {
        return;
      }
      scheduleIosPwaTextareaKeyboardRecovery();
    };

    const handleViewportResize = (): void => {
      if (isVirtualKeyboardOpen()) {
        return;
      }
      scheduleIosPwaTextareaKeyboardRecovery();
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', handleViewportResize);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      visualViewport?.removeEventListener('resize', handleViewportResize);
      resetIosPwaTextareaKeyboardRecovery();
    };
  }, [enabled]);
};
